import React, { useState } from "react";
import Space from "antd/lib/space";
import styled from "styled-components";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import { CircleIndicator } from "components/circleIndicator";
import { Task } from "./tasks";
import { createApiClient } from "apiClient";
import message from "antd/lib/message";
import { useMutation } from "react-query";

interface Props {
  task: Task;
  updateStatus: (taskId: string, status: Task["status"]) => void;
  updateOrderedItems: (
    taskId: string,
    itemId: number,
    quantity: number
  ) => void;
  userRole: string;
}

export const PatientDetail: React.FC<Props> = ({
  task,
  updateStatus,
  updateOrderedItems,
  userRole
}) => {
  const disabled = userRole !== task.implementorRole;

  const subTasks = task.subTasks ?? [];
  const consumables = task.consumables ?? [];
  const orderedItems = task.orderedItems ?? {};

  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

  const statusColors: any = {
    "Not Started": { inner: "#F31F13", outer: "#FBBBB8" },
    "In Progress": { inner: "#DD951F", outer: "#FCE5C6" },
    Completed: { inner: "#04A468", outer: "#B3E4D1" }
  };

  const allOrdered =
    consumables.length === 0 ||
    consumables.every(item => orderedItems[item.id]);

  // ✅ Mutation for placing order
  const { mutate: placeOrder, isLoading: isPlacingOrder } = useMutation(
    async ({ itemId, qty }: { itemId: number; qty: number }) => {
      return createApiClient().placeOrder(itemId, qty);
    },
    {
      onSuccess: (res, variables) => {
        message.success("Item ordered successfully");
        updateOrderedItems(task.id, variables.itemId, variables.qty);
      },
      onError: (err: any) => {
        message.error("Failed to place order");
      }
    }
  );

  return (
    <Container>
      <Card>
        {/* HEADER */}
        <Header>
          <Title>{task.taskName}</Title>
          <Role>{task.implementorRole}</Role>
        </Header>

        {/* DESCRIPTION */}
        <Description>{task.description}</Description>

        {/* STATUS */}
        <StatusRow>
          <CircleIndicator
            outerColor={statusColors[task.status].outer}
            innerColor={statusColors[task.status].inner}
          />
          <StatusText>{task.status}</StatusText>
        </StatusRow>

        {/* SUB TASKS */}
        {subTasks.length > 0 && (
          <Section>
            <SectionTitle>Sub Tasks</SectionTitle>
            <List>
              {subTasks.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </List>
          </Section>
        )}

        {/* CONSUMABLES */}
        {consumables.length > 0 && (
          <Section>
            <SectionTitle>Consumables</SectionTitle>

            {consumables.map((item, i) => {
              const qty = quantities[item.id];
              const isOrdered = orderedItems[item.id];

              return (
                <ConsumableRow key={item.id}>
                  <ConsumableName>{item.name}</ConsumableName>

                  <QuantityInput
                    type="number"
                    min={1}
                    placeholder="Qty"
                    value={qty || ""}
                    disabled={!!isOrdered}
                    onChange={e =>
                      setQuantities({
                        ...quantities,
                        [item.id]: Number(e.target.value)
                      })
                    }
                  />

                  {!isOrdered ? (
                    <AppButton
                      type={ButtonType.Primary}
                      buttonContent={"Order"}
                      disabled={!qty || qty <= 0 || isPlacingOrder}
                      onClick={() => placeOrder({ itemId: item.id, qty })}
                    />
                  ) : (
                    <OrderedTag>✔ Ordered ({isOrdered})</OrderedTag>
                  )}
                </ConsumableRow>
              );
            })}
          </Section>
        )}

        {/* KEY ACTION */}
        {/* {task.keyActions && (
          <Section>
            <SectionTitle>Key Action</SectionTitle>
            <KeyAction>{task.keyActions}</KeyAction>
          </Section>
        )} */}

        {/* ACTION BUTTONS */}
        <ActionSection>
          <Space>
            {task.status === "Not Started" && (
              <AppButton
                type={ButtonType.Primary}
                buttonContent={
                  task.id == "T-002"
                    ? "Send For Consent Approval"
                    : "Start Task"
                }
                disabled={disabled}
                onClick={() => updateStatus(task.id, "In Progress")}
              />
            )}

            {task.status === "In Progress" && (
              <AppButton
                type={ButtonType.Primary}
                buttonContent="Complete Task"
                disabled={disabled || !allOrdered}
                onClick={() => updateStatus(task.id, "Completed")}
              />
            )}

            {task.status === "Completed" && (
              <Completed>✔ Task Completed</Completed>
            )}
          </Space>
        </ActionSection>

        {/* NOTE */}
        {!allOrdered && task.status === "In Progress" && (
          <Note>Order all consumables before completing task</Note>
        )}

        {disabled && (
          <Note>Only {task.implementorRole} can perform this task</Note>
        )}
      </Card>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  display: flex;
  justify-content: center;
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 26px;
  width: 100%;
  max-width: 900px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Title = styled.h2`
  font-weight: 600;
  margin: 0;
`;
const Role = styled.div`
  background: #fafafa;
  border: 1px solid #eaeaea;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 13px;
`;
const Description = styled.p`
  margin-top: 12px;
  color: #555;
  line-height: 1.6;
`;
const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;
const StatusText = styled.span`
  font-weight: 600;
  font-size: 15px;
`;
const Section = styled.div`
  margin-top: 22px;
`;
const SectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 10px;
`;
const List = styled.ul`
  padding-left: 18px;
  margin: 0;
  li {
    margin-bottom: 6px;
    line-height: 1.5;
  }
`;
const ConsumableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;
const ConsumableName = styled.div`
  flex: 1;
  font-size: 14px;
`;
const QuantityInput = styled.input`
  width: 70px;
  padding: 6px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  outline: none;
  &:focus {
    border-color: #1890ff;
  }
`;
const KeyAction = styled.div`
  background: #fff7e6;
  border: 1px solid #ffe7ba;
  padding: 10px;
  border-radius: 6px;
  font-size: 14px;
`;
const ActionSection = styled.div`
  margin-top: 26px;
`;
const Completed = styled.div`
  color: #04a468;
  font-weight: 600;
  font-size: 15px;
`;
const Note = styled.div`
  margin-top: 15px;
  color: #ff4d4f;
  font-size: 13px;
`;
const OrderedTag = styled.div`
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  color: #389e0d;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
`;
